"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SubscriptionFeaturePicker } from "@/components/appointment/SubscriptionFeaturePicker";
import { appointmentService } from "@/services/appointmentService";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import { apiErrorMessage } from "@/lib/api";
import { UserSubscriptionFeature } from "@/types/userSubscription.types";
import { Appointment } from "@/types";
import { toast } from "sonner";

interface StaffFeatureUsageDialogProps {
  appointment: Appointment | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<void> | void;
}

/**
 * Lets staff record which subscription features were used on one of their
 * appointments. Only the feature selection is sent, so nothing else on the
 * appointment can be changed from here.
 */
export function StaffFeatureUsageDialog({
  appointment,
  onOpenChange,
  onSaved,
}: StaffFeatureUsageDialogProps) {
  const [features, setFeatures] = useState<UserSubscriptionFeature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [initialFeatureIds, setInitialFeatureIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const open = appointment !== null;

  useEffect(() => {
    if (!appointment) {
      return;
    }

    // Start from the features already used on this appointment
    const usedFeatureIds = (appointment.featuresUsed || []).map(
      (feature) => feature.featureId,
    );
    setSelectedFeatureIds(usedFeatureIds);
    setInitialFeatureIds(usedFeatureIds);

    if (!appointment.typeId) {
      setFeatures([]);
      setLoadFailed(true);
      return;
    }

    setLoadingFeatures(true);
    setLoadFailed(false);
    setFeatures([]);
    userSubscriptionService
      .getUserSubscriptionFeatures(appointment.typeId)
      .then((loadedFeatures) => {
        setFeatures(loadedFeatures);
      })
      .catch((error) => {
        console.error("Error fetching subscription features:", error);
        setLoadFailed(true);
        toast.error("Failed to load subscription features");
      })
      .finally(() => {
        setLoadingFeatures(false);
      });
  }, [appointment]);

  const handleToggle = (featureId: string) => {
    setSelectedFeatureIds((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId],
    );
  };

  const hasChanges =
    JSON.stringify([...selectedFeatureIds].sort()) !==
    JSON.stringify([...initialFeatureIds].sort());

  // Saving without the feature list loaded could wipe the recorded usage
  const canSave =
    !isSaving && !loadingFeatures && !loadFailed && features.length > 0 && hasChanges;

  const handleSave = async () => {
    if (!appointment || !canSave) return;

    setIsSaving(true);
    try {
      await appointmentService.updateAppointmentFeatures(
        appointment.id,
        selectedFeatureIds,
      );
      toast.success("Features updated");
      await onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save feature usage:", error);
      toast.error(apiErrorMessage(error, "Failed to update features"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isSaving) {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Subscription Features</DialogTitle>
          <DialogDescription>
            Tick the features used on this appointment. Each one is taken from
            the subscription balance.
          </DialogDescription>
        </DialogHeader>

        {loadFailed ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Subscription features could not be loaded.
          </div>
        ) : (
          <SubscriptionFeaturePicker
            features={features}
            loading={loadingFeatures}
            selectedFeatureIds={selectedFeatureIds}
            initialFeatureIds={initialFeatureIds}
            onToggle={handleToggle}
            disabled={isSaving}
          />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Close
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isSaving ? "Saving..." : "Save Features"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
